import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { toastr } from 'react-redux-toastr'
import _ from 'lodash'
import moment from "moment"
import './Files.css'
import {Upload, Trash2} from 'lucide-react'
import { defaultT } from '../utils/i18n'
import { Table, TrashCell, AutoSizer, Column } from './DataTable'
import { ProgressBar } from './ProgressBar/ProgressBar'
import axios from 'axios'
import withQuery from 'with-query'
import UploadHint from './UploadHint'
import { isTooLarge } from '../utils/uploadLimits'

const uploadPosterButtonStyle = {
    backgroundColor: '#e7f5e7',
    fontWeight: '300 !important',
    fontSize: '15px !important',
    borderStyle: 'dashed !important',
    width: '90%',
    height: '30px',
    border:' 1px solid #c1c3c5',
    borderRadius: '10px'
};

const deletePosterButtonStyle = {
    position: 'absolute',
    top: '4px',
    right: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: 'none',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    lineHeight: '18px',
    padding: 0,
    display: 'flex', justifyContent: 'center', alignItems: 'center'
}

const VideosUploader = props => {
    const [uploadState, setUploadState] = useState(0)

    // update the video table by adding a row
    const handleResponse = data => {
        const { push, user } = props
        if (data) {
            push({
                ...data,
                type: 'video',
                user
            })
        }
        else {
            toastr.error(t('errorUploading'))
        }
    }

    const handleOneVideoUpload = async (file, t) => {
        if (await isTooLarge(file, 'video')) {
            return toastr.error(t('tooLargeFile'))
        }

        const fileSize = file.size
        const chunkSize = 15 * 1024 * 1024 // 15MB
        const totalChunks = Math.ceil(fileSize / chunkSize)
        const cleanFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
        const uploadId = `${Date.now()}-${cleanFileName}` // unique per file

        for (let i = 0; i < totalChunks; i++) {
            const start = i * chunkSize
            const end = Math.min(start + chunkSize, fileSize)
            const fileChunk = file.slice(start, end)

            const data = new FormData()
            data.append('videoChunk', fileChunk, file.name)
            data.append('chunkIndex', i)
            data.append('chunksNb', totalChunks)
            data.append('fileName', file.name)
            data.append('fileSize', fileSize)
            data.append('user', JSON.stringify(props.user))
            data.append('uploadId', uploadId)

            const options = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'x-upload-id': uploadId,
                    'x-chunk-index': i,
                },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.floor((i / totalChunks) * 100) +
                        Math.floor((progressEvent.loaded / progressEvent.total) * (100 / totalChunks))
                    setUploadState(percent)
                }
            }

            try {
                await axios.post('/video/chunk', data, options)
            } catch (err) {
                const data = _.get(err, 'response.data', {})
                toastr.error(t(data.message || 'errorUploading', data))
                setUploadState(0)
                return // stop uploading if one chunk fails
            }
        }

        // Once all chunks uploaded, trigger merge and save to DB
        try {
            const res = await axios.post('/video/merge', {
                uploadId,
                fileName: file.name,
                fileSize,
                chunkNb: totalChunks,
                user: props.user,
            })
            handleResponse(res.data)
            toastr.success(t('successVideoUploading'))
        } catch (err) {
            const data = _.get(err, 'response.data', {})
            toastr.error(t(data.message || 'errorMergingChunks', data))
        } finally {
            setUploadState(0)
        }
    }

    const handleVideosUpload = t => async event => {
        const files = Array.from(event.target.files) // copy the FileList early
        event.target.value = null // reset to null to allow re-uploading the same files

        for (let i = 0; i < files.length; i++) {
            await handleOneVideoUpload(files[i], t)
        }
    }

    const handleClickTrash = (rowIndex, fileName, poster, t) => () => {
        toastr.confirm(t('deletionConfirmation'), {
            onOk: () => {
                // Delete video
                axios.delete(`/video/${fileName}`)
                    .then((res) => {
                        if(res.data.ok || res.data.code === "ENOENT") {
                            toastr.success(t('deletion'), t('successVideoDeleting'))
                            props.remove(rowIndex)
                        } else {
                            toastr.error(t('deletion'), res.data.message)
                        }
                    })

                // Delete poster if exists
                if (poster){
                    axios.delete(`/image/${poster}`)
                        .then((res) => {
                            if(res.data.ok || res.data.code === "ENOENT") {
                                toastr.success(t('deletion'), t('successImageDeleting'))
                            } else {
                                toastr.error(t('deletion'), res.data.message)
                            }
                        })
                }
            }
        })
    }
    const renderDeleteIcon = ({ rowIndex, rowData, t }) => {
        const fullDate = moment(rowData.date).format('YYYY-MM-DD_HH-mm-ss')
        const fileName = `${fullDate}_${rowData.name}`

        const fullPosterDate = rowData.poster?.date && moment(rowData.poster?.date).format('YYYY-MM-DD_HH-mm-ss')
        const poster = rowData.poster?.name && `/image/${fullPosterDate}_${rowData.poster?.name}`

        return <TrashCell onClick={handleClickTrash(rowIndex, fileName, poster, t)} />
    }

    const handlePosterUpload = (rowIndex, rowData, t) => async event => {
        const input = event.target // React pools the event, keep the input for after the await
        const files = input.files
        let data = new FormData()
        const fileSize = files[0].size
        const chunkSize = 15 * 1024 * 1024 // 15MB
        let fileChunk = null, chunkNb = 0

        if (await isTooLarge(files[0], 'image')) {
            toastr.error(t('tooLargeFile'))
        }
        else {
            for (let start = 0; start < fileSize; start += chunkSize, chunkNb++) {
                if (start + chunkSize > fileSize) {
                    fileChunk = files[0].slice(start, fileSize)
                }
                else {
                    fileChunk = files[0].slice(start, start + chunkSize)
                }
                data.append('imageChunks', fileChunk, files[0].name)
            }
            data.append('user', JSON.stringify(props.user))
            data.append('fileSize', fileSize)
            data.append('chunkNb', chunkNb)

            const options = {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const { loaded, total } = progressEvent
                    let percent = Math.floor( (loaded * 100) / total )
                    setUploadState(percent)
                }
            }
            axios.post('/image', data, options)
                .then(res => {
                    props.updateVideo(rowIndex, {...rowData, poster: {...res.data, type: 'image', user: props.user}})
                    toastr.success(t('successImageUploading'))
                    setUploadState(0)
                })
                .catch(err => {
                    const data = _.get(err, 'response.data', {})
                    toastr.error(t(data.message || 'errorUploading', data))
                    setUploadState(0)
                })
        }
        // Allow to re-upload the same file in a row
        input.value = null
    }

    const handleClickPosterTrash = (rowIndex, rowData, fileName, t) => () => {
        toastr.confirm(t('deletionConfirmation'), {
            onOk: () => axios.delete(`/image/${fileName}`)
                .then((res) => {
                    if(res.data.ok || res.data.code === "ENOENT") {
                        toastr.success(t('deletion'), t('successImageDeleting'))
                        props.updateVideo(rowIndex, {...rowData, poster: null})
                    } else {
                        toastr.error(t('deletion'), res.data.message)
                    }
                })
        })
    }

    const renderPoster = ({ rowIndex, rowData, t }) => {
        if (!rowData.poster) {
            return <div style={{position: 'relative', 'display': 'flex', 'justifyContent': 'left', 'alignItems': 'left'}}>
                <button style={uploadPosterButtonStyle}>
                    <Upload size={16} className="Files-Icon" />
                    <span className="Files-button-Text" style={{whiteSpace: 'normal'}}>
                        {t('addPoster', { capitalize: true })}
                    </span>
                </button>
                <input
                    className='Files-input'
                    type="file"
                    style={{ height: '100%', width: '100%'}}
                    accept="image/png, image/jpeg"
                    onChange={handlePosterUpload(rowIndex, rowData, t)}
                />
            </div>
        }

        const fullPosterDate = moment(rowData.poster.date).format('YYYY-MM-DD_HH-mm-ss')
        const poster = withQuery(`/image/${fullPosterDate}_${rowData.poster.name}`, {accessId: props.accessId, groupModelId: props.groupModel && props.groupModel.id})
        const posterFileName = `${fullPosterDate}_${rowData.poster.name}`

        return <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
            <div style={{ position: 'relative', width: '90%' }}>
                <img
                    src={poster}
                    alt="Video thumbnail"
                    style={{width: '100%', height: '30px', objectFit: 'cover', borderRadius: '10px',}}
                />
                <button
                    style={deletePosterButtonStyle}
                    onClick={handleClickPosterTrash(rowIndex, rowData, posterFileName, t)}
                >
                    <Trash2 size={16} style={{verticalAlign: 'middle', color: '#dd3c1b'}}/>
                </button>
            </div>
        </div>
    }

    const { allIds = [], byId = {}, t = defaultT } = props
    const objects = allIds.map(id => byId[id])

    const firstColumnLabel = props.tKey
    const uploadButtonPlaceholder = props.placeholder || 'addVideo(s)'

    let display = null
    if (props.editable) {
        display = <div className="Files-container">
                    <div className="Files-panel-body">
                        <AutoSizer disableHeight>
                            {({ width }) => (
                                <Table
                                    objects={objects}
                                    maxRows={7}
                                    width={width}
                                    noRowsLabel={t('noRows')}
                                >
                                    <Column
                                        flexGrow={1}
                                        width={200}
                                        dataKey="name"
                                        label={t(firstColumnLabel, { capitalize: true })}
                                        className='Video-DataTable-standard-column'
                                    />
                                    <Column
                                        flexGrow={0}
                                        width={220}
                                        dataKey="poster"
                                        label={t('poster', { capitalize: true })}
                                        cellRenderer={(data) => renderPoster({...data, t})}
                                        className='Video-DataTable-standard-column'
                                    />
                                    <Column
                                        flexGrow={0}
                                        width={220}
                                        dataKey="user.name"
                                        label={t('user', { capitalize: true })}
                                        cellDataGetter={({ dataKey, rowData }) => _.get(rowData, dataKey)}
                                        className='Video-DataTable-standard-column'
                                    />
                                    <Column
                                        flexGrow={0}
                                        width={160}
                                        dataKey="date"
                                        label={t('date', { capitalize: true })}
                                        className='Video-DataTable-standard-column'
                                    />
                                    <Column
                                        flexGrow={0}
                                        width={40}
                                        dataKey="id"
                                        label=""
                                        cellRenderer={(data) => renderDeleteIcon({...data, t})}
                                        className='Video-DataTable-standard-column'
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </div>
                    <div className="Files-input-group">
                        <button className="btn Files-button">
                            <Upload size={16} className="Files-Icon" />
                            <span className="Files-button-Text">
                                {t(uploadButtonPlaceholder, { capitalize: true })}
                            </span>
                        </button>
                        <input
                            className='Files-input'
                            type='file'
                            accept='video/mp4'
                            multiple
                            encType='multipart/form-data'
                            onChange={handleVideosUpload(t)}
                        />
                    </div>
                    <UploadHint kind="video" t={t} />
                    { uploadState > 0 ?
                        <div className="uploadProgress">
                            { t('uploading') }
                            <ProgressBar percentage={uploadState} color='#E94E24'/>
                            {uploadState}%
                        </div>
                        : null
                    }
                </div>
    }
    return display
}

VideosUploader.propTypes = {
    push: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    updateVideo: PropTypes.func.isRequired,
    t: PropTypes.func
}

export default VideosUploader
